import {
  type EnvironmentView,
  type GenerationView,
  type IngestionView,
  ingestionIn,
  type KbDocument,
} from "@cosmosai/api-client";

/** The environment whose ingestions show: the selected one, else the first development tier. */
export function shownEnvironment(
  selected: EnvironmentView | null,
  environments: readonly EnvironmentView[],
): string | null {
  return selected?.name ?? environments.find((env) => env.tier === "development")?.name ?? null;
}

/** Badge variants by ingestion status. */
export const INGESTION_VARIANT = {
  queued: "default",
  running: "info",
  ready: "ok",
  failed: "crit",
} as const;

/** Where a document stands in the environment shown: its ingestion there, or why there is none. */
export type DocumentState =
  | { kind: "deleting" }
  | { kind: "absent" }
  | { kind: "ingestion"; ingestion: IngestionView };

export function documentState(
  document: KbDocument,
  generations: readonly GenerationView[],
  environment: string | null,
): DocumentState {
  if (document.status === "deleting") return { kind: "deleting" };
  const ingestion = environment ? ingestionIn(document, generations, environment) : undefined;
  return ingestion ? { kind: "ingestion", ingestion } : { kind: "absent" };
}

/** Whether something still moves, so the tabs poll: an ingestion waiting, a generation building, a document going. */
export function isMoving(
  documents: readonly KbDocument[] | undefined,
  generations: readonly GenerationView[] | undefined,
): boolean {
  return (
    (documents ?? []).some(
      (document) =>
        document.status === "deleting" ||
        document.ingestions.some((row) => row.status === "queued" || row.status === "running"),
    ) || (generations ?? []).some((generation) => generation.status === "building")
  );
}

/** The types the platform reads, for the file picker. */
export const DOCUMENT_TYPES = ".pdf,.docx,.txt,.md,.html,.htm,.png,.jpg,.jpeg";
